import { Controller } from "@hotwired/stimulus";
import "./component.scss";

/**
 * Lesson Navigation Controller
 *
 * Handles keyboard navigation for lessons using arrow keys.
 * Left arrow navigates to previous lesson, right arrow to next.
 *
 * Note: This controller shares identical logic with playlist_item_navigation.
 * If you modify this file, consider updating the sister component as well.
 * See: app/frontend/components/playlist_item_navigation/component.js
 */
export default class extends Controller {
  static targets = ["previousLink", "nextLink", "navigation"];

  connect() {
    // HACK: ResizeObserver sizes the extra template wrapper to keep lessons clear of the fixed footer.
    // Replace this measured spacer with a frontend layout that allocates space for content and navigation.
    this.resizeObserver = new ResizeObserver(() => this.reserveSpace());
    this.resizeObserver.observe(this.navigationTarget);
    this.reserveSpace();
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener("keydown", this.handleKeydown);
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown);
    this.resizeObserver.disconnect();
  }

  reserveSpace() {
    const navigation = this.navigationTarget;
    this.element.style.height = getComputedStyle(navigation).position === "fixed"
      ? `${navigation.getBoundingClientRect().height}px`
      : "";
  }

  handleKeydown(event) {
    // Don't trigger if user is typing in an input field
    if (this.isInputFocused()) return;

    // Don't trigger if modifier keys are pressed
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)
      return;

    switch (event.key) {
      case "ArrowLeft":
        this.navigatePrevious(event);
        break;
      case "ArrowRight":
        this.navigateNext(event);
        break;
    }
  }

  navigatePrevious(event) {
    if (this.hasPreviousLinkTarget) {
      event.preventDefault();
      this.previousLinkTarget.click();
    }
  }

  navigateNext(event) {
    if (this.hasNextLinkTarget) {
      event.preventDefault();
      this.nextLinkTarget.click();
    }
  }

  isInputFocused() {
    const activeElement = document.activeElement;
    const tagName = activeElement?.tagName?.toLowerCase();

    return (
      tagName === "input" ||
      tagName === "textarea" ||
      tagName === "select" ||
      activeElement?.isContentEditable
    );
  }
}
